import React from "react";
import { hasItems } from "~/features/app/utils";
import { FilterBar, type Filter } from "~/features/filter/filter-bar";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
export const TYPE_LABELS: Record<string, string> = {
  blocks_editorialCardsArticleCard_BlockType: "Articles",
  blocks_editorialCardsCaseStudyCard_BlockType: "Case Studies",
  blocks_editorialCardsWebinarCard_BlockType: "Webinars",
  blocks_editorialCardsGeneralCard_BlockType: "General",
  blocks_editorialCardsShowcaseCard_BlockType: "Showcase"
};
type Card = {
  __typename?: string;
};
export function useEditorialCardsFilter<T extends Card>(cards: T[] | null | undefined, hasFilter: boolean) {
  return useEditorialCardsFilterBy<T>(cards, hasFilter, card => card.__typename, TYPE_LABELS);
}
export function useEditorialCardsFilterBy<T>(cards: T[] | null | undefined, hasFilter: boolean, getType: (card: T) => string | null | undefined, typeLabels: Record<string, string>) {
  const isLg = useMediaQuery(up("lg"));
  const typeOptions = React.useMemo(() => {
    if (!cards) return [];
    const types = cards.map(c => getType(c)).filter(Boolean) as string[];
    const unique = [...new Set(types)];
    if (unique.length <= 1) return [];
    return unique.map(t => ({
      label: typeLabels[t] ?? t,
      value: t
    }));
    // getType/typeLabels are stable per call site; memoize on cards only to
    // Keep filter behavior consistent across card types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);
  const [activeType, setActiveType] = React.useState("");
  const filteredCards = React.useMemo(() => {
    if (!cards) return [];
    if (!activeType) return cards;
    return cards.filter(c => getType(c) === activeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, activeType]);
  const filters: Filter[] = hasFilter && hasItems(typeOptions) ? [{
    title: "Type",
    options: typeOptions,
    type: isLg || typeOptions.length <= 4 ? "radio" : "dropdown",
    value: activeType
  }] : [];
  const filterBar = hasItems(filters) ? <FilterBar filters={filters} onChange={(_type, val) => setActiveType(val)} /> : undefined;
  return {
    filteredCards,
    filterBar,
    activeType
  };
}